import { Link } from 'react-router';
import { Badge, Checkbox, EmptyState, Panel, Skeleton } from '../../components/ui';
import { PageHeader } from '../../layouts/Shells';
import { useAuth } from '../auth/AuthProvider';
import { formatDate } from '../listView/TaskTable';
import { useCompleteMyTask, useMyTasks } from '../myTasks/hooks';
import { groupByDue, localIsoDate } from '../myTasks/types';
import { useInbox } from '../notifications/hooks';
import { unreadCount } from '../notifications/types';
import { useProjects } from '../projects/hooks';
import { useProjectProgress } from './hooks';

/**
 * Home (post-v1.0.0 enhancement): the morning glance. What needs you now
 * (your overdue and due-today tasks), where you work (recent projects), and
 * whether the Inbox wants attention. Everything here reuses the queries the
 * destination pages already run, so Home adds no new database surface.
 */

const PROJECTS_SHOWN = 6;
const TASKS_SHOWN = 7;

export function HomePage() {
  const { access } = useAuth();
  const firstName =
    access.status === 'approved' && access.profile.display_name.trim() !== ''
      ? access.profile.display_name.split(' ')[0]
      : '';

  const tasks = useMyTasks();
  const projects = useProjects();
  const inbox = useInbox();

  const buckets = groupByDue(tasks.data ?? [], localIsoDate(new Date()));
  const urgent = buckets
    .filter((bucket) => bucket.key === 'overdue' || bucket.key === 'today')
    .flatMap((bucket) => bucket.tasks.map((task) => ({ task, overdue: bucket.key === 'overdue' })))
    .slice(0, TASKS_SHOWN);
  const openTotal = buckets.reduce((sum, bucket) => sum + bucket.tasks.length, 0);
  const unread = unreadCount(inbox.data ?? []);
  const projectList = (projects.data ?? []).slice(0, PROJECTS_SHOWN);
  const progress = useProjectProgress(projectList.map((project) => project.id));
  const completeTask = useCompleteMyTask();

  return (
    <>
      <PageHeader
        title={firstName === '' ? 'Home' : `Welcome back, ${firstName}`}
        description="Your day at a glance."
      />

      {unread > 0 ? (
        <p className="home-inbox-line">
          <Badge tone="brand">{unread}</Badge>{' '}
          <Link to="/inbox">
            unread {unread === 1 ? 'notification' : 'notifications'} in your Inbox
          </Link>
        </p>
      ) : null}

      <Panel
        title="Needs you now"
        actions={
          <Link className="home-see-all" to="/home/tasks">
            All my tasks ({openTotal})
          </Link>
        }
      >
        {tasks.isPending ? (
          <Skeleton lines={3} label="Loading your tasks" />
        ) : urgent.length === 0 ? (
          <EmptyState
            title="Nothing overdue, nothing due today"
            description="Your open tasks live under My Tasks."
          />
        ) : (
          <ul className="my-task-list">
            {urgent.map(({ task, overdue }) => (
              <li key={task.id} className="my-task-row">
                <Checkbox
                  label={`Complete ${task.title}`}
                  labelHidden
                  checked={false}
                  onChange={() =>
                    completeTask.mutate({ taskId: task.id, projectId: task.project_id })
                  }
                />
                <div className="my-task-main">
                  <Link to={`/projects/${task.project_id}`} className="my-task-title">
                    {task.title}
                  </Link>
                  <span className="my-task-project">{task.projects?.name ?? ''}</span>
                </div>
                <span className={overdue ? 'my-task-due my-task-due--overdue' : 'my-task-due'}>
                  {overdue ? `Overdue: ${formatDate(task.due_date)}` : 'Due today'}
                </span>
              </li>
            ))}
          </ul>
        )}
      </Panel>

      <Panel
        title="Your projects"
        actions={
          <Link className="home-see-all" to="/projects">
            All projects
          </Link>
        }
      >
        {projects.isPending ? (
          <Skeleton lines={3} label="Loading projects" />
        ) : projectList.length === 0 ? (
          <EmptyState
            title="No projects yet"
            description="Create your first project from the Projects page."
          />
        ) : (
          <ul className="home-project-grid">
            {projectList.map((project) => {
              const stats = progress.data?.get(project.id) ?? { done: 0, total: 0 };
              const pct = stats.total === 0 ? 0 : Math.round((stats.done / stats.total) * 100);
              return (
                <li key={project.id}>
                  <Link to={`/projects/${project.id}`} className="home-project-card">
                    <span className="home-project-name">{project.name}</span>
                    <span className="home-project-progress" aria-hidden="true">
                      <span className="home-project-fill" style={{ width: `${pct}%` }} />
                    </span>
                    <span className="home-project-meta">
                      {stats.total === 0
                        ? 'No tasks yet'
                        : `${stats.done}/${stats.total} tasks · ${pct}%`}
                    </span>
                  </Link>
                </li>
              );
            })}
          </ul>
        )}
      </Panel>
    </>
  );
}
